/**
 * Reference Code Mapping for Plot Drives
 * Maps field names to Evaluate codes (resourceCode in Item Library API)
 */

export interface PlotDrivesReferenceCodeMapping {
  code?: string;
}

/**
 * Plot Drives Reference Codes
 * Maps field names from plot drives tables to Evaluate codes
 */
export const PLOT_DRIVES_REFERENCES: Record<
  string,
  PlotDrivesReferenceCodeMapping
> = {
  // Private Macadam - Drives to Binder
  macadamLevelCompact: { code: "GWALP005" },
  macadamGeotextileLayer: { code: "ALP031A" },
  macadamSubBase: { code: "GWALP006" },
  macadamDbBase: {}, // See Below - varies
  macadamDbBinder: {}, // See Below - varies
  macadamSealingGrit: { code: "ALP054" },

  // Private Macadam - Surface Course
  macadamRemoveTimberEdgings: { code: 'GWALP032a' }, // Remove Timber edgings (col48)
  macadamTack: { code: "ALP055" },
  macadamDbSurfaceCourse: {}, // See Below - varies
  macadamHraSurfaceCourse: {}, // See Below - varies
  macadamSmaSurfaceCourse: {}, // See Below - varies

  // Private Block Paved - Drives to Binder
  blockLevelCompact: { code: "GWALP005" },
  blockGeotextileLayer: { code: "ALP031A" },
  block300mmAdditionalCapping: { code: "GWALP006" },
  block250mmSubBase: { code: "GWALP006" },
  block100mmDbBase: {},
  blockDbBinder: {},
  blockSubBase: { code: "GWALP006" },
  blockBase: {},
  blockBinder: {},
  blockSealingGrit: { code: "ALP054" },

  // Private Block Paved - Surface Course
  blockSweep: { code: 'GWALPD020' },
  blockPunctureMacadam: { code: 'GWALPD022' },
  blockLayingCourse: { code: "GWALPD019" },
  block60mmConcrete: {}, // See Below - varies
  block80mmConcrete: {}, // See Below - varies

  // Permeable Paving - Drives to Binder
  permeableLevelCompact: { code: "GWALP005" },
  permeableImpermeableGeotextileLayer: { code: "GWALP014" },
  permeableDbBinderRunningCourse: {}, // Add in when required
  permeable10to63mmCleanAggregateSubBase: { code: "GWALP028" },
  permeable5to20mmCleanAggregateSubBase: { code: "GWALP028" },
  permeableGeotextileLayer: { code: "GWALP014a" },

  // Permeable Paving - Surface Course
  permeablePunctureMacadam: { code: 'GWALPD022' },
  permeable2to6mmLayingAggregate: { code: 'GWALP029' }, // thickness-converted via M3_RATE_THICKNESS_COLUMNS col59
  permeable60mmConcrete: {}, // See Below - varies
  permeable80mmConcrete: {}, // See Below - varies

  // Shared Macadam - Drives to Binder
  sharedMacadamLevelCompact: { code: "GWALP005" },
  sharedMacadamGeotextileLayer: { code: "ALP031A" },
  sharedMacadamSubBase: { code: "GWALP006" },
  sharedMacadamDbBase: {},
  sharedMacadamDbBinder: {},
  sharedMacadamSealingGrit: { code: "ALP054" },

  // Shared Macadam - Surface Course
  sharedMacadamSweepTack: { code: "ALP055" },
  sharedMacadamDbSurfaceCourse: {},
  sharedMacadamHraSurfaceCourse: {},
  sharedMacadamSmaSurfaceCourse: {},

  // Shared Block Paved - Drives to Binder
  sharedBlockLevelCompact: { code: "GWALP005" },
  sharedBlockGeotextileLayer: { code: "ALP031A" },
  sharedBlockSubBase: { code: "GWALP006" },
  sharedBlockBase: {},
  sharedBlockBinder: {},
  sharedBlockSealingGrit: { code: "ALP054" },

  // Shared Block Paved - Surface Course
  sharedBlockPunctureMacadam: { code: 'GWALPD022' },
  sharedBlockLayingCourse: { code: "GWALPD019" },
  sharedBlock60mmConcrete: {},
  sharedBlock80mmConcrete: {},

  // Shared Permeable - Drives to Binder
  sharedPermeableLevelCompact: { code: "GWALP005" },
  sharedPermeableImpermeableGeotextileLayer: { code: "GWALP014" },
  sharedPermeableDbBinderRunningCourse: {},
  sharedPermeable10to63mmCleanAggregateSubBase: { code: "GWALP028" },
  sharedPermeable5to20mmCleanAggregateSubBase: { code: "GWALP028" },
  sharedPermeableGeotextileLayer: { code: "GWALP014a" },

  // Shared Permeable - Surface Course & Edgings
  sharedPermeablePunctureMacadam: { code: 'GWALPD022' },
  sharedPermeable2to6mmLayingAggregate: { code: "GWALP029" },
  sharedPermeable60mmConcrete: {},
  sharedPermeable80mmConcrete: {},
  sharedPermeable150x50mmPccEdgings: { code: "ALP027" },
  sharedPermeable200x50mmPccEdgings: { code: "ALP027A" },
  sharedPermeableTimberEdgings: { code: "GWALP032" },
  sharedPermablePccKerbs: { code: "ALP083" },
  sharedPermeableKerbType1: {},
  sharedPermeableKerbType2: {},
  sharedPermeableKerbType3: {},
  sharedPermeableKeyKerb: { code: "ALP103" },
  sharedPermeableRecessedCF: { code: "GWALPD013" },
  sharedPermeableAdjustIronwork: { code: "GWALPD011" },
  sharedPermeableDriveDemo: { code: "GWALPD012" },
  sharedPermeableEdgingRemedials: { code: "ALP085" },
  sharedPermeableKerbRemedials: { code: "ALP086" },
};

/**
 * Column code mapping using col## dataIndex keys (as used in PlotDrives table columns)
 */
export const PLOT_DRIVES_COL_CODES: Record<string, { code?: string }> = {
  // Private Macadam Binder
  col1: { code: 'GWALP005' },   // Level & Compact
  col2: { code: 'ALP031A' },    // Geotextile Layer
  col3: { code: 'GWALP006' },  // MOT Type 1
  col4: {},
  col5: {},
  col6: { code: 'ALP054' },     // Sealing Grit
  // Private Block Paved Binder
  col7: { code: 'GWALP005' },   // Level & Compact
  col8: { code: 'ALP031A' },    // Geotextile Layer
  col9: { code: 'GWALP006' },  // MOT Type 1
  col10: {},
  col11: {},
  col12: { code: 'ALP054' },    // Sealing Grit
  // Private Permeable Binder
  col14: { code: 'GWALP005' },  // Level & Compact
  col15: { code: 'GWALP014' },  // Permeable geotextile membrane
  col16: {},
  col17: { code: 'GWALP028' },  // 63-10mm Sub Base
  col18: { code: 'GWALP028' },  // 5-20mm Sub Base
  col19: { code: 'GWALP014a' }, // Permeable geotextile membrane
  // Shared Macadam Binder
  col20: { code: 'GWALP005' },  // Level & Compact
  col21: { code: 'ALP031A' },   // Geotextile Layer
  col22: { code: 'GWALP006' }, // MOT Type 1
  col23: {},
  col24: {},
  col25: { code: 'ALP054' },    // Sealing Grit
  // Shared Block Paved Binder
  col26: { code: 'GWALP005' },  // Level & Compact
  col27: { code: 'ALP031A' },   // Geotextile Layer
  col28: { code: 'GWALP006' }, // MOT Type 1
  col29: {},
  col30: {},
  col31: { code: 'ALP054' },    // Sealing Grit
  // Shared Permeable Binder
  col33: { code: 'GWALP005' },  // Level & Compact
  col34: { code: 'GWALP014' },  // Permeable geotextile membrane
  col35: {},
  col36: { code: 'GWALP028' },  // 63-10mm Sub Base
  col37: { code: 'GWALP028' },  // 5-20mm Sub Base
  col38: { code: 'GWALP014a' }, // Permeable geotextile membrane
  // Kerbs & Edgings
  col39: { code: 'ALP027' },    // 150x50mm PCC Edgings
  col40: { code: 'ALP027A' },   // 200x50mm PCC Edgings
  col41: { code: 'GWALP032' },  // Timber Edgings
  col42: { code: 'ALP083' },    // PCC Kerbs
  col43: {},
  col44: {},
  col45: {},
  col46: { code: 'ALP103' },    // Key Kerb
  col47: { code: 'GWALPD013' }, // Recessed C&F
  // Private Macadam Surface Course
  col48: { code: 'GWALP032a' }, // Remove Timber edgings
  col49: { code: 'ALP055' },    // Sweep & Tack
  col50: {},
  col51: {},
  col52: {},
  // Private Block Surface Course
  col53: { code: 'GWALPD020' }, // Sweep
  col54: { code: 'GWALPD022' }, // Puncture Macadam
  col55: { code: 'GWALPD019' },  // Sand Laying Course
  col56: {},
  col57: {},
  // Private Permeable Surface Course
  col58: { code: 'GWALPD022' }, // Puncture Macadam
  col59: { code:'GWALP029'},
  col60: {},
  col61: {},
  // Shared Macadam Surface Course
  col62: { code: 'ALP055' },    // Sweep & Tack
  col63: {},
  col64: {},
  col65: {},
  // Shared Block Surface Course
  col66: { code: 'GWALPD022' }, // Puncture Macadam
  col67: { code: 'GWALPD019' },  // Sand Laying Course
  col68: {},
  col69: {},
  // Shared Permeable Surface Course & Ancillaries
  col70: { code: 'GWALPD022' }, // Puncture Macadam
  col71: { code: 'GWALP029' },
  col72: {},
  col73: {},
  col74: { code: 'GWALPD011' },   // Adjust Ironwork
  col75: { code: 'GWALPD012' }, // Drive Demarcation
  col76: { code: 'ALP085' },    // Edging Remedials
  col77: { code: 'ALP086' },    // Kerb Remedials
};

/**
 * Get reference code for a specific field
 */
export const getPlotDrivesReferenceCode = (
  referenceKey: string,
): string | undefined => {
  return PLOT_DRIVES_REFERENCES[referenceKey]?.code;
};

/**
 * Get full reference information for a specific field
 */
export const getPlotDrivesReference = (
  referenceKey: string,
): PlotDrivesReferenceCodeMapping | undefined => {
  return PLOT_DRIVES_REFERENCES[referenceKey];
};
